import React, { useState } from 'react'

const JobForm = ({ job, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    title: job?.title || '',
    company: job?.company || '',
    location: job?.location || '',
    sector: job?.sector || '',
    type: job?.type || 'Full-time',
    salary: job?.salary || '',
    active: job?.active !== undefined ? job.active : true,
    description: job?.description || '',
    requirements: job?.requirements || '',
    links: job?.links
      ? job.links.map(l => ({ ...l, _id: l._id ?? Date.now() + Math.random() }))
      : [{ _id: Date.now(), text: 'Apply Now', url: '' }],
    expiryDate: job?.expiryDate || ''
  })

  const [errors, setErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  const sectors = [
    'Engineering', 'Health Care', 'Finance', 'Communications', 
    'Transport', 'Property', 'Security', 'Hospitality', 
    'Project Mgmt', 'Administration', 'Skilled Trades', 'Technology',
    'Construction', 'Manufacturing', 'Retail', 'Education',
    'Agriculture', 'Professional Services'
  ]

  const jobTypes = [
    'Full-time', 'Part-time', 'Contract', 'Temporary', 'Internship'
  ]

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
  }

  const handleLinkChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      links: prev.links.map((l, i) => i === index ? { ...l, [field]: value } : l)
    }))
  }

  const addLink = () => {
    setFormData(prev => ({
      ...prev,
      links: [...prev.links, { _id: Date.now() + Math.random(), text: 'Apply Now', url: '' }]
    }))
  }

  const removeLink = (index) => {
    setFormData(prev => ({
      ...prev,
      links: prev.links.filter((_, i) => i !== index)
    }))
  }

  const validate = () => {
    const newErrors = {}
    
    if (!formData.title.trim()) newErrors.title = 'Title is required'
    if (!formData.company.trim()) newErrors.company = 'Company is required'
    if (!formData.location.trim()) newErrors.location = 'Location is required'
    if (!formData.sector) newErrors.sector = 'Sector is required'
    if (!formData.type) newErrors.type = 'Type is required'
    
    formData.links.forEach((link, index) => {
      if (link.url && !link.text.trim()) {
        newErrors[`link_text_${index}`] = 'Link text is required when URL is provided'
      }
      if (link.text && !link.url.trim()) {
        newErrors[`link_url_${index}`] = 'Link URL is required when text is provided'
      }
    })
    
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validate()) return
    
    setIsSubmitting(true)
    
    try {
      const cleanedData = {
        ...formData,
        links: formData.links
          .filter(link => link.text.trim() && link.url.trim())
          .map(({ _id: _, ...link }) => link)
      }
      
      await onSubmit(cleanedData)
    } catch {
      // error propagates to parent via onSubmit rejection
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="job-form-overlay">
      <div className="job-form-modal">
        <div className="job-form-header">
          <h3>{job ? 'Edit Job' : 'Add New Job'}</h3>
          <button className="btn-close" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="job-form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="title">Job Title *</label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                className={errors.title ? 'error' : ''}
                placeholder="e.g. Senior Software Engineer"
              />
              {errors.title && <span className="error-message">{errors.title}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="company">Company *</label>
              <input
                type="text"
                id="company"
                name="company"
                value={formData.company}
                onChange={handleChange}
                className={errors.company ? 'error' : ''}
                placeholder="e.g. Tech Corp NZ"
              />
              {errors.company && <span className="error-message">{errors.company}</span>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="expiryDate">Expiry Date <span style={{fontWeight:'normal',color:'var(--text-medium)'}}>(optional)</span></label>
              <input
                type="date"
                id="expiryDate"
                name="expiryDate"
                value={formData.expiryDate}
                onChange={handleChange}
                min={new Date().toISOString().split('T')[0]}
              />
            </div>
            <div className="form-group">
              <label htmlFor="location">Location *</label>
              <input
                type="text"
                id="location"
                name="location"
                value={formData.location}
                onChange={handleChange}
                className={errors.location ? 'error' : ''}
                placeholder="e.g. Auckland, New Zealand"
              />
              {errors.location && <span className="error-message">{errors.location}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="salary">Salary</label>
              <input
                type="text"
                id="salary"
                name="salary"
                value={formData.salary}
                onChange={handleChange}
                placeholder="e.g. $80k - $100k or Competitive"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="sector">Sector *</label>
              <select
                id="sector"
                name="sector"
                value={formData.sector}
                onChange={handleChange}
                className={errors.sector ? 'error' : ''}
              >
                <option value="">Select a sector</option>
                {sectors.map(sector => (
                  <option key={sector} value={sector}>{sector}</option>
                ))}
              </select>
              {errors.sector && <span className="error-message">{errors.sector}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="type">Job Type *</label>
              <select
                id="type"
                name="type"
                value={formData.type}
                onChange={handleChange}
                className={errors.type ? 'error' : ''}
              >
                {jobTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
              {errors.type && <span className="error-message">{errors.type}</span>}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="description">Job Description</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="4"
              placeholder="Detailed job description..."
            />
          </div>

          <div className="form-group">
            <label htmlFor="requirements">Requirements</label>
            <textarea
              id="requirements"
              name="requirements"
              value={formData.requirements}
              onChange={handleChange}
              rows="3"
              placeholder="Job requirements and qualifications..."
            />
          </div>

          <div className="form-group">
            <div className="checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="active"
                  checked={formData.active}
                  onChange={handleChange}
                />
                <span className="checkmark"></span>
                Active (show on website)
              </label>
            </div>
          </div>

          <div className="form-group">
            <label>Application Links</label>
            {formData.links.map((link, index) => (
              <div key={link._id} className="link-row">
                <input
                  type="text"
                  placeholder="Link text (e.g. Apply Now)"
                  value={link.text}
                  onChange={(e) => handleLinkChange(index, 'text', e.target.value)}
                  className={errors[`link_text_${index}`] ? 'error' : ''}
                />
                <input
                  type="url"
                  placeholder="https://example.com/apply"
                  value={link.url}
                  onChange={(e) => handleLinkChange(index, 'url', e.target.value)}
                  className={errors[`link_url_${index}`] ? 'error' : ''}
                />
                {formData.links.length > 1 && (
                  <button
                    type="button"
                    className="btn btn-sm btn-danger"
                    onClick={() => removeLink(index)}
                  >
                    <i className="fas fa-trash"></i>
                  </button>
                )}
              </div>
            ))}
            {errors[`link_text_${0}`] && <span className="error-message">{errors[`link_text_${0}`]}</span>}
            {errors[`link_url_${0}`] && <span className="error-message">{errors[`link_url_${0}`]}</span>}
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={addLink}
            >
              <i className="fas fa-plus"></i>
              Add Another Link
            </button>
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <div className="spinner"></div>
                  Saving...
                </>
              ) : (
                <>
                  <i className="fas fa-save"></i>
                  {job ? 'Update Job' : 'Create Job'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default JobForm
